import type { SerializedError } from '@reduxjs/toolkit';
import { useMemo } from 'react';

import { useGetProfileEligibilityNgemrPatientQuery } from '@shared/services/profileEligibility';
import { useGetProfileBasicQuery, useGetProfilePatientMasterQuery, userProfileApi } from '@shared/services/userProfile';
import { PROFILE_BASIC_TAGS } from '@shared/services/userProfile/endpoints/getProfileBasic';
import { PROFILE_PATIENT_MASTER_TAGS } from '@shared/services/userProfile/endpoints/getProfilePatientMaster';
import type { GetProfileBasicPayload, ProfileBasic } from '@shared/services/userProfile/types/getProfileBasic.types';
import type { GetProfilePatientMasterPayload } from '@shared/services/userProfile/types/getProfilePatientMaster.types';
import { useAppDispatch } from '@shared/stores/rootTypes';
import type { ProfileFull } from '@shared/types/user.types';

import useIsAuthenticated from '../useIsAuthenticated';

interface UseCurrentUserBaseReturn {
  clearUserProfile: () => void;
  isFetching: boolean;
  isLoading: boolean;
  refetchUser: () => void;
  userProfileError?: SerializedError;
}

/** Returned when `isPatientMasterInfoRequired` is `true` */
interface UseCurrentUserBasicProfileReturn extends UseCurrentUserBaseReturn {
  hasNoPhoneNumber: false;
  userProfile?: ProfileBasic;
}

/** Returned when `isPatientMasterInfoRequired` is `false` */
interface UseCurrentUserParams<T extends boolean = false> {
  getProfileBasicPayload?: GetProfileBasicPayload;
  getProfilePatientMasterPayload?: GetProfilePatientMasterPayload;
  inSequenceCall?: boolean;
  isPatientMasterInfoRequired?: T;
  secure?: boolean;
  skip?: boolean;
}

interface UseCurrentUserPatientMasterProfileReturn extends UseCurrentUserBaseReturn {
  hasNoPhoneNumber: boolean;
  mismatchStatus?: number;
  userProfile?: ProfileFull;
}

type UseCurrentUserReturn<T extends boolean> = T extends true
  ? UseCurrentUserPatientMasterProfileReturn
  : UseCurrentUserBasicProfileReturn;

const useCurrentUserV2 = <T extends boolean = false>({
  skip = false,
  secure = false,
  isPatientMasterInfoRequired = false as T,
  getProfileBasicPayload,
  getProfilePatientMasterPayload,
  inSequenceCall = false,
}: UseCurrentUserParams<T> = {}): UseCurrentUserReturn<T> => {
  const { isAuthenticated } = useIsAuthenticated();
  const dispatch = useAppDispatch();

  const {
    data: profileBasic,
    error: profileBasicError,
    isFetching: isFetchingProfileBasic,
    isLoading: isLoadingProfileBasic,
    refetch: refetchProfileBasic,
  } = useGetProfileBasicQuery(
    {
      secure,
      ...getProfileBasicPayload,
    },
    {
      skip: !isAuthenticated || skip,
    }
  );

  const {
    data: profilePatientMaster,
    error: profilePatientMasterError,
    isFetching: isFetchingProfilePatientMaster,
    isLoading: isLoadingProfilePatientMaster,
    refetch: refetchProfilePatientMaster,
  } = useGetProfilePatientMasterQuery(
    {
      secure,
      ...getProfilePatientMasterPayload,
    },
    {
      skip: !isAuthenticated || skip || !isPatientMasterInfoRequired || (inSequenceCall ? !profileBasic : false),
    }
  );

  const memberPrn = getProfileBasicPayload?.MemberPrn ?? getProfilePatientMasterPayload?.MemberPrn;
  const { data: ngemrPatient, refetch: refetchNgemrPatient } = useGetProfileEligibilityNgemrPatientQuery(
    {
      MemberPrn: memberPrn,
    },
    {
      skip: !isAuthenticated || skip,
    }
  );

  const mergedUserProfile = useMemo(
    () => ({
      ...profileBasic?.Profile,
      ...profilePatientMaster?.Profile,
      IsNGEMRPatient: ngemrPatient?.IsNGEMRPatient,
    }),
    [profileBasic?.Profile, profilePatientMaster?.Profile, ngemrPatient?.IsNGEMRPatient]
  );

  const patientMasterCurrentUserReturn = useMemo(
    () => ({
      userProfile: mergedUserProfile,
      userProfileError: profileBasicError ?? profilePatientMasterError,
      isFetching: isFetchingProfileBasic || isFetchingProfilePatientMaster,
      isLoading: isLoadingProfileBasic || isLoadingProfilePatientMaster,
      refetchUser: () => {
        refetchProfileBasic();
        refetchProfilePatientMaster();
        refetchNgemrPatient();
      },
      hasNoPhoneNumber: !profilePatientMaster?.Profile?.ContactNumber,
      mismatchStatus: profilePatientMaster?.MismatchStatus,
      clearUserProfile: () => {
        dispatch(userProfileApi.util.invalidateTags([PROFILE_BASIC_TAGS, PROFILE_PATIENT_MASTER_TAGS]));
      },
    }),
    [
      dispatch,
      mergedUserProfile,
      profileBasicError,
      profilePatientMasterError,
      isFetchingProfileBasic,
      isFetchingProfilePatientMaster,
      isLoadingProfileBasic,
      isLoadingProfilePatientMaster,
      refetchNgemrPatient,
      refetchProfileBasic,
      refetchProfilePatientMaster,
      profilePatientMaster?.Profile?.ContactNumber,
      profilePatientMaster?.MismatchStatus,
    ]
  );

  const profileBasicCurrentUserReturn = useMemo(
    () => ({
      userProfile: profileBasic?.Profile
        ? { ...profileBasic.Profile, IsNGEMRPatient: ngemrPatient?.IsNGEMRPatient }
        : undefined,
      userProfileError: profileBasicError,
      isFetching: isFetchingProfileBasic,
      isLoading: isLoadingProfileBasic,
      refetchUser: refetchProfileBasic,
      hasNoPhoneNumber: false,
      clearUserProfile: () => {
        dispatch(userProfileApi.util.invalidateTags([PROFILE_BASIC_TAGS]));
      },
    }),
    [
      dispatch,
      profileBasic?.Profile,
      profileBasicError,
      isFetchingProfileBasic,
      isLoadingProfileBasic,
      refetchProfileBasic,
      ngemrPatient?.IsNGEMRPatient,
    ]
  );

  if (isPatientMasterInfoRequired) {
    return patientMasterCurrentUserReturn as UseCurrentUserReturn<T>;
  }

  return profileBasicCurrentUserReturn as UseCurrentUserReturn<T>;
};

export { useCurrentUserV2 };
